import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { http, formatApiError, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Stethoscope, Upload, CheckCircle2, X, ShieldCheck, FileText } from "lucide-react";

const DOC_SLOTS = [
  { key: "practitioner_photo", label: "Profile Photo", accept: "image/*", required: true, field: "photo_doc_id" },
  { key: "bams_cert", label: "BAMS Degree Certificate", accept: "application/pdf,image/*", required: true, field: "bams_cert_doc_id" },
  { key: "council_cert", label: "Council Registration Certificate", accept: "application/pdf,image/*", required: true, field: "council_cert_doc_id" },
];

export default function PractitionerJoin() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    full_name: user?.name || "",
    display_title: "Dr.",
    bams_reg_no: "",
    ncism_council: "",
    council_reg_no: "",
    years_experience: 5,
    specializations: "",
    languages: "",
    consultation_fee: 800,
    consultation_duration_min: 30,
    clinic_city: "",
    bio: "",
  });
  const [documents, setDocuments] = useState({});
  const [uploading, setUploading] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (location.state?.agreement_accepted) setAccepted(true);
  }, [location.state]);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const commission = (Number(form.consultation_fee) * 20) / 100;
  const doctorGets = Number(form.consultation_fee) - commission;

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10MB"); return; }
    setUploading(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`/vendors/documents/upload?doc_type=${docType}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocuments((p) => ({ ...p, [docType]: data }));
      toast.success(`${DOC_SLOTS.find((d) => d.key === docType)?.label} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user || user === false) { toast.error("Please sign in first"); nav("/signup"); return; }
    if (!accepted) { toast.error("Please accept the Practitioner Agreement"); return; }
    const missing = DOC_SLOTS.filter((d) => d.required && !documents[d.key]);
    if (missing.length) { toast.error(`Please upload: ${missing.map((m) => m.label).join(", ")}`); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        years_experience: parseInt(form.years_experience),
        consultation_fee: parseFloat(form.consultation_fee),
        consultation_duration_min: parseInt(form.consultation_duration_min),
        specializations: form.specializations.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        agreement_accepted: true,
        photo_doc_id: documents.practitioner_photo?.id,
        bams_cert_doc_id: documents.bams_cert?.id,
        council_cert_doc_id: documents.council_cert?.id,
      };
      await http.post("/practitioners/apply", payload);
      await refresh();
      toast.success("Application submitted. Amiroop team will verify in 48 hrs.");
      nav("/practitioner/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="p-10 text-center">Loading…</div>;
  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <h1 className="font-serif text-3xl font-bold text-[#1A201C]">Join as an Ayurvedic Practitioner</h1>
        <p className="mt-2 text-[#5A6B5D]">Please sign up to apply.</p>
        <Link to="/signup" className="mt-6 inline-flex btn-forest">Create an account</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="practitioner-join-page">
      <div className="mb-8">
        <span className="badge-sage inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" /> Step 2 of 2 · KYC, Credentials & Fee</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-bold text-[#1A201C] tracking-tight">Join as an Ayurvedic Practitioner</h1>
        <p className="mt-3 text-[#5A6B5D] max-w-3xl">
          Practice on your terms. Set your own consultation fee, own your schedule, and consult patients across India through Healthion India.
          Amiroop Healthion LLP handles payments, scheduling and support — for a flat <strong>20% platform commission</strong>.
        </p>
        {accepted ? (
          <div className="mt-3 inline-flex items-center gap-2 text-xs font-mono text-[#1E3A2B] bg-[#E8F0EA] border border-[#1E3A2B]/20 rounded-full px-3 py-1" data-testid="prac-agreement-badge">
            <ShieldCheck className="w-3 h-3" /> Practitioner Onboarding Agreement accepted
          </div>
        ) : (
          <Link to="/practitioner/agreement" className="mt-3 inline-flex items-center gap-2 text-xs font-mono text-[#D97706] bg-white border border-[#D97706]/30 rounded-full px-3 py-1 hover:bg-[#D97706]/10">
            <FileText className="w-3 h-3" /> Read & accept the Practitioner Agreement first →
          </Link>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Perk title="You set the fee" body="Practitioners decide their consultation price. You keep 80% of every session." />
        <Perk title="Verified profile" body="Verified BAMS & Council badge builds trust with informed customers." />
        <Perk title="Zero setup cost" body="No listing fee, no monthly charge. We only earn when you consult." />
      </div>

      <form onSubmit={submit} className="bg-white border border-[#E2DDD5] rounded-3xl p-6 sm:p-8 space-y-6" data-testid="practitioner-apply-form">
        <div>
          <h2 className="font-serif text-xl font-semibold text-[#1E3A2B]">Personal & Practice Details</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 grid grid-cols-[100px_1fr] gap-3">
              <div>
                <label className="text-xs font-mono uppercase text-[#5A6B5D]">Title</label>
                <select value={form.display_title} onChange={(e) => set("display_title", e.target.value)} className="mt-1 w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm">
                  <option>Dr.</option><option>Vaidya</option>
                </select>
              </div>
              <Field label="Full Name" value={form.full_name} onChange={(v) => set("full_name", v)} testid="prac-name" />
            </div>
            <Field label="BAMS Registration Number" value={form.bams_reg_no} onChange={(v) => set("bams_reg_no", v)} testid="prac-bams" />
            <Field label="Council Registration Number" value={form.council_reg_no} onChange={(v) => set("council_reg_no", v)} testid="prac-council-no" />
            <Field label="Registered With (NCISM / State Council)" value={form.ncism_council} onChange={(v) => set("ncism_council", v)} testid="prac-council-name" cls="sm:col-span-2" />
            <Field label="Years of Experience" type="number" value={form.years_experience} onChange={(v) => set("years_experience", v)} testid="prac-years" />
            <Field label="City / Clinic Location" value={form.clinic_city} onChange={(v) => set("clinic_city", v)} testid="prac-city" />
            <Field label="Specialisations (comma-separated)" value={form.specializations} onChange={(v) => set("specializations", v)} testid="prac-specializations" cls="sm:col-span-2" placeholder="Panchakarma, Skin & Hair, Diabetes Support" />
            <Field label="Languages Spoken (comma-separated)" value={form.languages} onChange={(v) => set("languages", v)} testid="prac-languages" cls="sm:col-span-2" placeholder="English, Hindi, Marathi" />
          </div>
          <div className="mt-3">
            <label className="text-xs font-mono uppercase text-[#5A6B5D]">About You / Practice Story</label>
            <textarea required value={form.bio} onChange={(e) => set("bio", e.target.value)} className="mt-1 w-full border border-[#E2DDD5] rounded-lg p-3 text-sm min-h-[100px]" placeholder="Tell patients about your approach, training and focus areas..." data-testid="prac-bio" />
          </div>
        </div>

        <div className="border-t border-[#E2DDD5] pt-6">
          <h2 className="font-serif text-xl font-semibold text-[#1E3A2B]">Consultation & Fee</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4 items-start">
            <div>
              <label className="text-xs font-mono uppercase text-[#5A6B5D]">Your Consultation Fee (₹)</label>
              <input required type="number" min="0" value={form.consultation_fee} onChange={(e) => set("consultation_fee", e.target.value)} className="mt-1 w-full border border-[#E2DDD5] rounded-lg p-3 text-lg font-serif font-bold text-[#1E3A2B]" data-testid="prac-fee" />
              <div className="mt-1 text-[10px] font-mono uppercase text-[#5A6B5D]">You keep 80%. Amiroop retains 20%.</div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase text-[#5A6B5D]">Session Duration (minutes)</label>
              <select value={form.consultation_duration_min} onChange={(e) => set("consultation_duration_min", e.target.value)} className="mt-1 w-full border border-[#E2DDD5] rounded-lg p-3 text-sm">
                {[15, 20, 30, 45, 60].map((n) => <option key={n} value={n}>{n} minutes</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 bg-[#E8F0EA] border border-[#1E3A2B]/20 rounded-xl p-4 text-sm space-y-1">
            <div className="flex justify-between font-mono"><span>Patient pays</span><span className="font-bold">{formatINR(form.consultation_fee || 0)}</span></div>
            <div className="flex justify-between font-mono text-[#5A6B5D]"><span>Amiroop Healthion commission (20%)</span><span>−{formatINR(commission)}</span></div>
            <div className="flex justify-between font-mono font-bold text-[#1E3A2B] pt-1 border-t border-[#1E3A2B]/10"><span>You earn per session</span><span>{formatINR(doctorGets)}</span></div>
          </div>
        </div>

        <div className="border-t border-[#E2DDD5] pt-6">
          <h2 className="font-serif text-xl font-semibold text-[#1E3A2B]">KYC Documents & Photo</h2>
          <p className="text-xs text-[#5A6B5D] mt-1">Upload high-quality copies. Max 10 MB each · PDF, JPG, PNG, WEBP.</p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {DOC_SLOTS.map((d) => {
              const uploaded = documents[d.key];
              const busy = uploading === d.key;
              return (
                <div key={d.key} className="bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl p-3" data-testid={`prac-doc-${d.key}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-[#1A201C]">{d.label}{d.required && <span className="text-[#D97706]"> *</span>}</div>
                      {uploaded ? (
                        <div className="mt-1 flex items-center gap-1 text-xs text-[#1E3A2B] font-mono truncate">
                          <CheckCircle2 className="w-3 h-3 shrink-0" /> <span className="truncate">{uploaded.filename}</span>
                        </div>
                      ) : (<div className="mt-1 text-xs text-[#5A6B5D]">Not uploaded</div>)}
                    </div>
                    {uploaded && (
                      <button type="button" onClick={() => setDocuments((p) => { const n = { ...p }; delete n[d.key]; return n; })} className="text-[#5A6B5D] hover:text-red-600">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <label className={`mt-2 cursor-pointer inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${uploaded ? "border-[#E2DDD5] text-[#5A6B5D]" : "bg-[#1E3A2B] text-white border-[#1E3A2B]"} ${busy ? "opacity-60" : ""}`}>
                    <Upload className="w-3 h-3" /> {busy ? "Uploading…" : uploaded ? "Replace" : "Upload"}
                    <input type="file" accept={d.accept} onChange={(e) => uploadDoc(d.key, e.target.files?.[0])} className="hidden" disabled={busy} data-testid={`prac-doc-input-${d.key}`} />
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        <label className="flex items-start gap-3 p-4 bg-[#FAF8F5] border border-[#E2DDD5] rounded-xl cursor-pointer">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#1E3A2B]" data-testid="prac-agreement-checkbox" />
          <span className="text-sm text-[#1A201C]">
            I have read and accept the{" "}
            <Link to="/practitioner/agreement" target="_blank" className="text-[#1E3A2B] font-semibold underline inline-flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" /> Ayurvedic Practitioner Onboarding Agreement
            </Link>
            {" "}— including 20% platform commission, twice-monthly payouts (1st & 16th), independent-contractor status, indemnity clauses, and jurisdiction of courts in Lucknow, Uttar Pradesh.
          </span>
        </label>

        <button disabled={submitting} className="w-full btn-forest" data-testid="prac-submit">
          {submitting ? "Submitting…" : "Submit for Verification"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, cls = "", placeholder }) {
  return (
    <div className={cls}>
      <label className="text-xs font-mono uppercase text-[#5A6B5D]">{label}</label>
      <input required type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="mt-1 w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm bg-white" />
    </div>
  );
}

function Perk({ title, body }) {
  return (
    <div className="card-warm p-5">
      <CheckCircle2 className="w-5 h-5 text-[#D97706]" />
      <div className="mt-2 font-serif text-lg font-semibold text-[#1A201C]">{title}</div>
      <div className="mt-1 text-sm text-[#5A6B5D]">{body}</div>
    </div>
  );
}
